import { useState } from 'react';
import Modal from 'react-modal';

const TERMS_SECTIONS = [
  {
    title: 'Agreement and Scope',
    terms: [
      ['001', 'Parties', 'These terms govern the tenant-screening checkout services offered by ClearRent Systems LLC (ClearRent) to an applicant (you).'],
      ['002', 'Effective version', 'The version presented and accepted at checkout governs that transaction, subject to mandatory law.'],
      ['003', 'Scope', 'These terms address screening ordering, payment, and related platform functions, not the terms of any future lease.'],
      ['004', 'Other agreements', 'A landlord’s rental criteria, Checkr’s applicable notices, and ClearRent’s privacy notice may apply separately.'],
      ['005', 'Order of precedence', 'Mandatory law and legally required screening disclosures control over conflicting language in these terms.'],
      ['006', 'Eligibility', 'You must have legal capacity to enter the transaction or act through a legally authorized representative.'],
      ['007', 'Accurate identity', 'You agree to provide accurate identifying information and not impersonate another person.'],
      ['008', 'One applicant per screening', 'A screening order applies only to the identified individual and may not be transferred to another applicant.'],
      ['009', 'No housing offer', 'A screening order is not an offer of housing, reservation, deposit, or lease.'],
      ['010', 'Landlord independence', 'The landlord or authorized property manager, not ClearRent, makes the housing decision.'],
    ],
  },
  {
    title: 'Fees and Checkout',
    terms: [
      ['011', 'Displayed total', 'ClearRent will display the screening package, total price, and applicable fees before payment authorization.'],
      ['012', 'Affirmative acceptance', 'Payment terms must be accepted through an unchecked checkbox or other affirmative action before purchase.'],
      ['013', 'Payment authorization', 'By confirming purchase, you authorize the displayed one-time charge through the identified payment processor.'],
      ['014', 'No recurring screening charge', 'A single screening checkout does not authorize recurring charges unless separately and clearly agreed.'],
      ['015', 'Currency', 'Unless expressly stated otherwise, prices are displayed and charged in U.S. dollars.'],
      ['016', 'Price changes', 'A price change will not retroactively increase the amount authorized for a completed checkout.'],
      ['017', 'Separate services', 'Optional services must be separately identified, priced, and affirmatively selected.'],
      ['018', 'Receipt', 'A receipt or transaction confirmation will be made available electronically.'],
      ['019', 'Duplicate payments', 'Verified duplicate charges for the same service will be corrected or refunded.'],
      ['020', 'Unauthorized charges', 'Claims of unauthorized payment will be investigated and handled consistently with applicable law and payment-network rules.'],
    ],
  },
  {
    title: 'Refunds and Cancellations',
    terms: [
      ['021', 'Nonrefundable after commencement', 'The screening fee is ordinarily nonrefundable after screening processing begins, except where required by law or this policy.'],
      ['022', 'Pre-processing cancellation', 'Requests made before screening begins will be reviewed for cancellation and refund eligibility.'],
      ['023', 'Unfulfilled screening', 'Where a paid screening cannot be initiated or delivered, ClearRent will arrange an appropriate refund or remedy as required.'],
      ['024', 'Provider exceptions', 'Any applicable Checkr refund eligibility and payment-flow rules will be honored to the extent they govern the transaction.'],
      ['025', 'Denial not refund basis', 'A lawful rental denial alone does not ordinarily entitle an applicant to a screening-fee refund.'],
      ['026', 'Change of mind', 'Changing your mind after processing begins does not ordinarily create a refund entitlement.'],
      ['027', 'Report disagreement', 'Disagreement with report content does not automatically cancel the screening charge; report disputes remain available.'],
      ['028', 'Property mismatch', 'If the screening was initiated for an unrecognized or incorrect property, contact support promptly for review.'],
      ['029', 'Timing of refunds', 'Approved refunds are issued through the applicable processor; posting time depends on the financial institution.'],
      ['030', 'No waiver of payment rights', 'Nothing restricts statutory refund rights, chargeback rights, or the ability to report unauthorized transactions.'],
    ],
  },
  {
    title: 'Screening Authorization and FCRA',
    terms: [
      ['031', 'Separate consent', 'Acceptance of these payment terms is not itself the separate screening authorization collected by Checkr.'],
      ['032', 'Permissible purpose', 'Consumer reports may be ordered and used only for legally permissible housing-related purposes.'],
      ['033', 'Screening disclosures', 'Applicants must receive applicable screening disclosures and complete the required authorization workflow.'],
      ['034', 'Checkr workflow', 'Where integrated, Checkr may collect identity data and consent through its own secure applicant flow.'],
      ['035', 'Correct requester', 'ClearRent and participating landlords must identify the actual report recipient or original requester as required by the provider.'],
      ['036', 'Restricted use', 'Screening information may not be used for employment, marketing, harassment, or other unauthorized purposes.'],
      ['037', 'No resale', 'Applicants and landlords may not sell, publish, or redistribute reports except as legally permitted.'],
      ['038', 'Report access', 'Only verified and authorized parties with a permissible purpose may access consumer reports.'],
      ['039', 'Report disputes', 'Applicants retain all rights to obtain, review, and dispute reports with the responsible consumer reporting agency.'],
      ['040', 'No rights waiver', 'These terms do not waive rights or remedies under the Fair Credit Reporting Act or other applicable consumer reporting laws.'],
    ],
  },
  {
    title: 'Landlord and Housing Decisions',
    terms: [
      ['041', 'Landlord criteria', 'Landlords are responsible for establishing and applying lawful, consistent rental criteria.'],
      ['042', 'Fair housing', 'Users must comply with federal, state, and local fair housing and anti-discrimination requirements.'],
      ['043', 'Individualized review', 'Landlords must not assume a screening result alone conclusively establishes suitability for housing.'],
      ['044', 'Criminal records', 'Landlords must evaluate criminal-history information consistently with applicable fair housing guidance and law.'],
      ['045', 'Adverse action', 'A landlord taking adverse action based partly or wholly on a consumer report must provide legally required notices.'],
      ['046', 'Adverse action scope', 'Denial, higher deposits, co-signer requirements, or less favorable terms may trigger notice duties.'],
      ['047', 'Credit disclosures', 'Where applicable, required credit-score disclosures must accompany adverse-action communications.'],
      ['048', 'Independent decisions', 'ClearRent does not direct or guarantee a landlord’s approval, rejection, pricing, or lease terms.'],
      ['049', 'Application status', 'A screening completion status does not represent an approval, lease offer, or eligibility determination.'],
      ['050', 'Rental availability', 'Listings and unit availability may change independently of screening processing.'],
    ],
  },
  {
    title: 'Applicant Responsibilities',
    terms: [
      ['051', 'Truthful submissions', 'You agree not to knowingly submit false or materially misleading information.'],
      ['052', 'Document rights', 'You may upload only documents you have the right to provide.'],
      ['053', 'Account security', 'You are responsible for reasonable steps to protect your login credentials and devices.'],
      ['054', 'Prompt correction', 'Notify support promptly of an incorrect email, property, applicant identity, or duplicate order.'],
      ['055', 'Authorized payment', 'Use only payment methods you are authorized to use.'],
      ['056', 'No interference', 'Do not interfere with identity verification, screening, or fraud-detection systems.'],
      ['057', 'No automation abuse', 'Do not use bots or scripts to evade checkout, screening, or account safeguards.'],
      ['058', 'No unauthorized access', 'Do not attempt to view another applicant’s report, application, or private information.'],
      ['059', 'Lawful conduct', 'Do not use ClearRent to threaten, harass, discriminate, or commit fraud.'],
      ['060', 'Cooperation', 'Reasonable cooperation may be requested to investigate a legitimate payment, fraud, or account-security concern.'],
    ],
  },
  {
    title: 'Privacy and Information Security',
    terms: [
      ['061', 'Privacy notice', 'Personal information is handled as described in ClearRent’s published privacy notice and applicable law.'],
      ['062', 'Data minimization', 'ClearRent will seek to collect only information reasonably necessary for its stated services.'],
      ['063', 'Provider collection', 'Where feasible, sensitive screening data should be submitted directly to Checkr rather than stored by ClearRent.'],
      ['064', 'Limited sharing', 'Information may be shared with authorized landlords, service providers, or authorities only as permitted or required.'],
      ['065', 'Security safeguards', 'ClearRent will use reasonable administrative, technical, and organizational safeguards appropriate to the information.'],
      ['066', 'No absolute security promise', 'No internet-based system can guarantee perfect security; this clause does not excuse legally required safeguards.'],
      ['067', 'Access controls', 'Report access should be restricted using appropriate account verification, permissions, and audit controls.'],
      ['068', 'Retention', 'Records will be retained only for legitimate operational, legal, or compliance purposes under applicable schedules.'],
      ['069', 'Deletion requests', 'Privacy requests will be evaluated under applicable law, retention duties, and identity-verification requirements.'],
      ['070', 'Incident response', 'Suspected data incidents will be investigated, and legally required notifications will be provided.'],
    ],
  },
  {
    title: 'Third Parties and Platform Operations',
    terms: [
      ['071', 'Third-party screening', 'Checkr Tenant or another disclosed provider may perform the actual screening and generate the report.'],
      ['072', 'Provider independence', 'Third-party providers control their own screening methods, data sources, and applicable service procedures.'],
      ['073', 'No content guarantee', 'ClearRent does not independently certify the accuracy or completeness of third-party report content.'],
      ['074', 'No blanket disclaimer', 'This allocation of responsibility does not remove liability that law places on ClearRent for its own acts or omissions.'],
      ['075', 'Payment processor', 'Payment information may be processed by a disclosed payment service under its applicable terms.'],
      ['076', 'Service interruptions', 'Temporary interruptions may occur for maintenance, outages, or external system failures.'],
      ['077', 'Reasonable remedies', 'If a paid service fails, ClearRent will assess remedies consistent with these terms and applicable law.'],
      ['078', 'Updates', 'ClearRent may update technical functionality without retroactively changing an accepted payment bargain.'],
      ['079', 'Support channel', 'Applicants may contact the published ClearRent support channel about billing and platform problems.'],
      ['080', 'Recordkeeping', 'ClearRent may retain legally appropriate evidence of checkout acceptance, consent status, and transaction history.'],
    ],
  },
  {
    title: 'Liability and Dispute Resolution',
    terms: [
      ['081', 'Direct losses', 'To the extent permitted by law, liability for ordinary platform contract claims may be limited to foreseeable direct losses.'],
      ['082', 'No prohibited cap', 'Any liability limitation excludes claims or remedies that cannot legally be limited.'],
      ['083', 'No indirect damages', 'To the extent permitted by law, ClearRent disclaims liability for remote or consequential losses not reasonably foreseeable.'],
      ['084', 'No landlord guarantee', 'ClearRent does not guarantee landlord conduct, property condition, tenancy, or the outcome of a rental application.'],
      ['085', 'No report outcome guarantee', 'ClearRent does not guarantee a particular score, record finding, turnaround time, or screening result.'],
      ['086', 'Good-faith investigation', 'Parties are encouraged to contact support to attempt informal resolution of billing or service issues.'],
      ['087', 'Court access', 'Nothing prevents a party from pursuing a claim in a court or agency where the law provides that right.'],
      ['088', 'Regulatory complaints', 'Nothing prohibits reports to regulators, consumer reporting agencies, or law enforcement.'],
      ['089', 'Applicable law', 'These terms are governed by applicable federal law and the otherwise applicable state law, without displacing mandatory consumer protections.'],
      ['090', 'Severability', 'If one clause is unenforceable, the remainder continues to the extent legally permitted.'],
    ],
  },
  {
    title: 'Administration and Acceptance',
    terms: [
      ['091', 'Electronic records', 'You agree that transactional notices and records may be provided electronically subject to applicable consent requirements.'],
      ['092', 'Accessible copy', 'You may review or save these terms before payment and request a copy through support.'],
      ['093', 'Material changes', 'Material changes apply prospectively and will be presented when required.'],
      ['094', 'No implied agency', 'These terms do not create an agency, employment, partnership, or fiduciary relationship with applicants.'],
      ['095', 'No transfer of rights', 'You may not transfer an individual screening order to another person.'],
      ['096', 'Lawful enforcement', 'ClearRent may investigate misuse and restrict access where reasonably necessary and legally permitted.'],
      ['097', 'Consistent application', 'ClearRent will apply published payment terms without unlawful discrimination.'],
      ['098', 'Contact details', 'ClearRent will maintain a working support contact and publish its current business contact details.'],
      ['099', 'Full agreement', 'For the screening checkout, these terms and expressly incorporated disclosures constitute the applicable agreement, subject to law.'],
      ['100', 'Final acknowledgment', 'By checking the separate payment-terms box, you confirm that you had the opportunity to read these terms before authorizing payment.'],
    ],
  },
];

const LEGAL_REFERENCES = [
  ['Fair Credit Reporting Act, 15 U.S.C. 1681 et seq.', 'https://www.ftc.gov/legal-library/browse/statutes/fair-credit-reporting-act'],
  ['Permissible purposes, 15 U.S.C. 1681b', 'https://uscode.house.gov/us/usc/119-118/t15/s1681b/'],
  ['Adverse action, 15 U.S.C. 1681m', 'https://www.govinfo.gov/content/pkg/USCODE-2024-title15/html/USCODE-2024-title15-chap41-subchapIII-sec1681m.htm'],
  ['FTC guidance for landlords', 'https://www.ftc.gov/business-guidance/resources/using-consumer-reports-what-landlords-need-know'],
  ['Checkr Tenant API and consent', 'https://tenant.checkr.com/developers'],
  ['Checkr Tenant refund FAQ', 'https://tenant.checkr.com/resources/faq'],
];

const BackgroundCheckTermsModal = ({ isOpen, onClose }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    contentLabel="Tenant Screening Payment Terms and Conditions"
    shouldCloseOnOverlayClick={true}
    ariaHideApp={false}
    className="logout-modal background-check-terms-modal"
    overlayClassName="logout-modal-overlay"
  >
    <button className="logout-modal-close" onClick={onClose} type="button" aria-label="Close">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
    <h2 className="background-check-terms-title">Tenant Screening Payment Terms &amp; Conditions</h2>
    <p className="background-check-terms-company">ClearRent Systems LLC</p>
    <p className="background-check-terms-meta">
      Effective date: [INSERT DATE] | Support: [INSERT EMAIL] | Website: [INSERT URL]
    </p>

    <div className="background-check-terms-body">
      <p>
        <strong>Important:</strong> This document addresses the screening checkout only. It is not a substitute for
        Checkr’s separate screening disclosure and authorization, a privacy policy, or landlord adverse-action notices.
        Applicable consumer rights prevail over conflicting terms.
      </p>
      <p>
        <strong>Checkout notice:</strong> The exact screening fee and package must appear before purchase. Screening
        fees are ordinarily nonrefundable once processing begins, subject to required refunds and the exceptions
        described below. Payment does not guarantee rental approval.
      </p>

      {TERMS_SECTIONS.map((section) => (
        <section key={section.title}>
          <h3>{section.title}</h3>
          {section.terms.map(([number, name, text]) => (
            <p key={number}>
              {number}. <strong>{name}.</strong> {text}
            </p>
          ))}
        </section>
      ))}

      <section>
        <h3>Legal references</h3>
        <ul>
          {LEGAL_REFERENCES.map(([label, url]) => (
            <li key={url}>
              <a href={url} target="_blank" rel="noopener noreferrer">{label}</a>
            </li>
          ))}
        </ul>
      </section>
    </div>

    <div className="background-check-terms-footer">
      <button type="button" className="background-check-start-btn" onClick={onClose}>Close</button>
    </div>
  </Modal>
);

// Required before any background check payment; the parent keeps the Start/Pay button disabled until checked
const BackgroundCheckTermsConsent = ({ checked, onChange }) => {
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  return (
    <>
      <label className="background-check-terms-consent">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>
          I have reviewed and agree to the{' '}
          <button type="button" className="background-check-terms-link" onClick={() => setIsTermsOpen(true)}>
            Tenant Screening Payment Terms
          </button>
          , including the nonrefundable-fee policy and applicable exceptions. I understand that separate
          Checkr screening disclosures and authorization may be required.
        </span>
      </label>
      <BackgroundCheckTermsModal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
    </>
  );
};

export default BackgroundCheckTermsConsent;
